import { z } from 'zod';

import { Temporal } from 'temporal-polyfill';
import { classTimeSchema } from './class-time.js';

export const STUDENT_GROUP_NOTES_MAX_LENGTH = 200;

export const studentGroupStatusSchema = z.enum(['active', 'archived']);
export const StudentGroupStatus = studentGroupStatusSchema.enum;
export type StudentGroupStatus = z.infer<typeof studentGroupStatusSchema>;
export const StudentGroupStatusLabelMap: Record<StudentGroupStatus, string> = {
  active: 'Active',
  archived: 'Archived',
};

export const coachAttendanceStatusSchema = z.enum(['Perfect', 'IssuesFound', 'GroupNotFound']);
export type CoachAttendanceStatus = z.infer<typeof coachAttendanceStatusSchema>;
export const CoachAttendanceStatus = coachAttendanceStatusSchema.enum;
export const CoachAttendanceStatusLabelMap: Record<CoachAttendanceStatus, string> = {
  Perfect: '✅ Perfect',
  IssuesFound: 'Issues found',
  GroupNotFound: 'Group not found',
};

export const attendanceStatusSchema = z.enum(['at_risk', 'triggered', 'reassigned']);
export type AttendanceStatus = z.infer<typeof attendanceStatusSchema>;
export const AttendanceStatus = attendanceStatusSchema.enum;
export const AttendanceStatusLabelMap: Record<AttendanceStatus, string> = {
  at_risk: 'At risk',
  triggered: 'Triggered',
  reassigned: 'Reassigned',
};
export const attendanceStatusSelectableSchema = attendanceStatusSchema.or(z.literal('null'));
export type AttendanceStatusSelectable = z.infer<typeof attendanceStatusSelectableSchema>;
export const attendanceStatusSelectableValues: AttendanceStatusSelectable[] = [
  'null',
  ...attendanceStatusSchema.options,
];

export const emailsSentSchema = z.enum([
  'first_warning',
  'maximum_limit',
  'reassignment',
  'late_entry_warning',
  'late_entry_final_warning',
  'early_exit_warning',
  'early_exit_final_warning',
  'substitute_1_remaining',
  'substitute_at_allowance',
  'substitute_1_over',
]);
export type EmailsSent = z.infer<typeof emailsSentSchema>;
export const EmailsSent = emailsSentSchema.enum;
export const EmailsSentLabelMap: Record<EmailsSent, string> = {
  first_warning: '1st warning',
  maximum_limit: 'Maximum limit',
  reassignment: 'Reassignment',
  late_entry_warning: 'Late entry: Warning',
  late_entry_final_warning: 'Late entry: Final warning',
  early_exit_warning: 'Early exit: Warning',
  early_exit_final_warning: 'Early exit: Final warning',
  substitute_1_remaining: 'Substitute: 1 remaining',
  substitute_at_allowance: 'Substitute: At allowance',
  substitute_1_over: 'Substitute: 1 over',
};

/** Offered while teacher-attendance-policy-v2-admin is off. */
export const emailsSentLegacySelectableValues = [
  EmailsSent.first_warning,
  EmailsSent.maximum_limit,
  EmailsSent.reassignment,
] as const satisfies readonly EmailsSent[];

/**
 * Offered while teacher-attendance-policy-v2-admin is on.
 * Late-entry and early-exit warning is the second occurrence; final warning is the third.
 * Legacy first_warning and maximum_limit stay stored and readable, but are not offered for new selection.
 */
export const emailsSentV2SelectableValues = [
  EmailsSent.late_entry_warning,
  EmailsSent.late_entry_final_warning,
  EmailsSent.early_exit_warning,
  EmailsSent.early_exit_final_warning,
  EmailsSent.substitute_1_remaining,
  EmailsSent.substitute_at_allowance,
  EmailsSent.substitute_1_over,
  EmailsSent.reassignment,
] as const satisfies readonly EmailsSent[];

export function emailsSentSelectableValues(attendancePolicyV2AdminEnabled: boolean): readonly EmailsSent[] {
  return attendancePolicyV2AdminEnabled ? emailsSentV2SelectableValues : emailsSentLegacySelectableValues;
}

export const CoachAttendanceTagLabelMap = {
  Sub: 'Regular sub',
  SubExcused: 'Regular sub (excused)',
  SubEmergency: 'Short notice sub',
  SubEmergencyExcused: 'Short notice sub (excused)',
  TeacherNoShow: 'Teacher no show',
  Late: 'Late',
  createLateTag: (minutes: number): `(Late - ${number} mins)` => `(Late - ${minutes} mins)`,
} as const;

export const coachAttendanceTagSchema = z.union([
  z.literal(CoachAttendanceTagLabelMap.Sub),
  z.literal(CoachAttendanceTagLabelMap.SubExcused),
  z.literal(CoachAttendanceTagLabelMap.SubEmergency),
  z.literal(CoachAttendanceTagLabelMap.SubEmergencyExcused),
  z.literal(CoachAttendanceTagLabelMap.TeacherNoShow),
  z.literal(CoachAttendanceTagLabelMap.Late),
  z.string().regex(/^\(Late - \d+ mins\)$/),
]);
export type CoachAttendanceTag =
  | typeof CoachAttendanceTagLabelMap.Sub
  | typeof CoachAttendanceTagLabelMap.SubExcused
  | typeof CoachAttendanceTagLabelMap.SubEmergency
  | typeof CoachAttendanceTagLabelMap.SubEmergencyExcused
  | typeof CoachAttendanceTagLabelMap.TeacherNoShow
  | typeof CoachAttendanceTagLabelMap.Late
  | `(Late - ${number} mins)`;

export const CoachAttendanceLateTagRegex = /^\(Late - (\d+) mins\)$/;

export const coachAttendanceRollupItemSchema = z.object({
  plainDate: z.instanceof(Temporal.PlainDate),
  tags: z.array(coachAttendanceTagSchema),
  reasonsByTag: z.record(z.string(), z.string()).optional(),
});
export type CoachAttendanceRollupItem = z.infer<typeof coachAttendanceRollupItemSchema>;

export const getCoachAttendanceRollupResultSchema = z.object({
  status: coachAttendanceStatusSchema,
  items: z.array(coachAttendanceRollupItemSchema),
});
export type GetCoachAttendanceRollupResult = z.infer<typeof getCoachAttendanceRollupResultSchema>;

export const studentGroupSchema = z.object({
  id: z.string(),
  coachId: z.string().nullable(),
  learningPathId: z.string(),
  startingLessonId: z.string(),
  classTimeId: z.string(),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  status: studentGroupStatusSchema,
  coachAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  coachLastAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  coachLastUnassignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  coachAttendanceRollup: getCoachAttendanceRollupResultSchema.optional(),
  attendanceStatus: attendanceStatusSchema.nullable(),
  emailsSent: emailsSentSchema.array(),
  attendanceNotes: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.instanceof(Temporal.Instant),
  updatedAt: z.instanceof(Temporal.Instant),
  lastManualUpdatedAt: z.instanceof(Temporal.Instant).nullable(),
});
export type StudentGroup = z.infer<typeof studentGroupSchema>;

export const studentGroupWithClassTimeSchema = studentGroupSchema.extend({
  classTime: z.lazy(() => classTimeSchema),
});
